import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag, Loader2, Clock } from 'lucide-react';
import DOMPurify from 'dompurify';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = window.location.origin;

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/articles/${slug}`);
        setArticle(res.data);
        // Update OG meta tags dynamically
        const a = res.data;
        document.title = a.meta_title || a.titulo;
        setMetaTag('og:title', a.meta_title || a.titulo);
        setMetaTag('og:description', a.meta_description || a.resumen);
        setMetaTag('og:image', a.imagen);
        setMetaTag('og:url', `${SITE_URL}/blog/${a.slug}`);
        setMetaTag('og:type', 'article');
        setMetaTag('description', a.meta_description || a.resumen);
      } catch {
        setError('Artículo no encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    return () => { document.title = 'PrevenciónLegal | Tecnoabogado'; };
  }, [slug]);

  function setMetaTag(property, content) {
    const attr = property.startsWith('og:') ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${property}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content || '');
  }

  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.trim().split('\n');
    const elements = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (!trimmed) continue;

      if (trimmed.startsWith('## ')) {
        elements.push(<h2 key={i} className="text-2xl font-bold text-slate-900 mt-10 mb-4">{trimmed.slice(3)}</h2>);
      } else if (trimmed.startsWith('### ')) {
        elements.push(<h3 key={i} className="text-lg font-bold text-slate-900 mt-8 mb-3">{trimmed.slice(4)}</h3>);
      } else if (trimmed.startsWith('> ')) {
        elements.push(
          <blockquote key={i} className="border-l-4 border-rose-500 pl-4 py-2 my-6 bg-rose-50 italic text-slate-700">
            {trimmed.slice(2)}
          </blockquote>
        );
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const items = [trimmed.slice(2)];
        while (i + 1 < lines.length && (lines[i + 1].trim().startsWith('- ') || lines[i + 1].trim().startsWith('* '))) {
          i++;
          items.push(lines[i].trim().slice(2));
        }
        elements.push(
          <ul key={i} className="list-disc pl-6 space-y-2 my-4 text-slate-600">
            {items.map((item, j) => <li key={`ul-${i}-${j}`} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />)}
          </ul>
        );
      } else if (/^\d+[\.\)]/.test(trimmed)) {
        const items = [trimmed.replace(/^\d+[\.\)]\s*/, '')];
        while (i + 1 < lines.length && /^\d+[\.\)]/.test(lines[i + 1].trim())) {
          i++;
          items.push(lines[i].trim().replace(/^\d+[\.\)]\s*/, ''));
        }
        elements.push(
          <ol key={i} className="list-decimal pl-6 space-y-2 my-4 text-slate-600">
            {items.map((item, j) => <li key={`ol-${i}-${j}`} dangerouslySetInnerHTML={{ __html: formatInline(item) }} />)}
          </ol>
        );
      } else if (trimmed.startsWith('|')) {
        const tableLines = [trimmed];
        while (i + 1 < lines.length && lines[i + 1].trim().startsWith('|')) {
          i++;
          tableLines.push(lines[i].trim());
        }
        const rows = tableLines.filter(l => !l.match(/^\|[-\s|]+\|$/));
        if (rows.length > 1) {
          const headers = rows[0].split('|').filter(c => c.trim());
          const body = rows.slice(1);
          elements.push(
            <div key={i} className="overflow-x-auto my-6">
              <table className="w-full text-sm border border-slate-200">
                <thead>
                  <tr className="bg-slate-100">
                    {headers.map((h, j) => <th key={`th-${j}-${h.trim()}`} className="px-4 py-2 text-left font-bold text-slate-900 border-b border-slate-200">{h.trim()}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, j) => {
                    const cells = row.split('|').filter(c => c.trim());
                    return (
                      <tr key={`row-${j}`} className="border-b border-slate-100">
                        {cells.map((c, k) => <td key={`cell-${j}-${k}`} className="px-4 py-2 text-slate-600" dangerouslySetInnerHTML={{ __html: formatInline(c.trim()) }} />)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
      } else {
        elements.push(<p key={i} className="text-slate-600 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />);
      }
    }
    return elements;
  };

  const formatInline = (text) => {
    const raw = text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-slate-900">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-slate-100 px-1 py-0.5 text-sm">$1</code>');
    return DOMPurify.sanitize(raw, { ALLOWED_TAGS: ['strong', 'em', 'code'], ALLOWED_ATTR: ['class'] });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-rose-600" size={32} />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">{error || 'Artículo no encontrado'}</h1>
          <Link to="/blog" className="text-rose-600 hover:underline">Volver al blog</Link>
        </div>
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/blog/${article.slug}`;
  const hasHtml = article.content_html && article.content_html.trim().length > 50;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="article-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={18} />
            <span className="text-sm">Blog</span>
          </Link>
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" alt="PrevenciónLegal @tecnoabogado" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {article.imagen && (
          <div className="w-full h-64 md:h-80 overflow-hidden mb-8">
            <img src={article.imagen} alt={article.titulo} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-rose-600">
            <Tag size={12} />{article.categoria}
          </span>
          {article.created_at && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <Calendar size={12} />
              {new Date(article.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
          )}
          {article.read_time && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <Clock size={12} />{article.read_time}
            </span>
          )}
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-4" data-testid="article-title">
          {article.titulo}
        </h1>

        <p className="text-lg text-slate-600 mb-6 leading-relaxed">{article.resumen}</p>

        {/* Share buttons top */}
        <div className="pb-6 mb-8 border-b border-slate-200">
          <ShareButtons title={article.titulo} url={articleUrl} description={article.resumen} />
        </div>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map(tag => (
              <span key={tag} className="text-xs px-3 py-1 border border-slate-200 text-slate-500">{tag}</span>
            ))}
          </div>
        )}

        {/* Content: prefer HTML, fallback to markdown */}
        <div className="prose-article" data-testid="article-content">
          {hasHtml ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content_html, {
              ALLOWED_TAGS: ['h1','h2','h3','h4','h5','h6','p','a','strong','em','code','pre','ul','ol','li','blockquote','table','thead','tbody','tr','th','td','div','span','section','nav','header','footer','article','figure','figcaption','img','br','hr'],
              ALLOWED_ATTR: ['class','id','href','target','rel','src','alt','style','aria-label','role','colspan','rowspan']
            }) }} />
          ) : (
            renderContent(article.contenido)
          )}
        </div>

        {/* Share buttons bottom */}
        <div className="pt-8 mt-12 border-t border-slate-200">
          <ShareButtons title={article.titulo} url={articleUrl} description={article.resumen} />
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-slate-900 text-white">
          <h3 className="text-xl font-bold mb-3">¿Necesita asesoramiento sobre este tema?</h3>
          <p className="text-slate-300 mb-6 text-sm">Nuestro equipo puede ayudarle con su caso concreto.</p>
          <button onClick={() => navigate('/', { state: { scrollTo: 'contacto' } })} className="inline-block px-6 py-3 bg-rose-600 text-white font-bold hover:bg-rose-700 transition" data-testid="article-cta">
            Solicitar consulta
          </button>
        </div>
      </main>
    </div>
  );
}
